/**
 * REGISTRATION-PAUSE (FNP-1222) — TEMPORARY.
 *
 * Self-registration is closed in the portal while other issues are fixed.
 * This is the single switch for that pause. Everything else that changed for
 * FNP-1222 is a guard that reads `registration.isEnabled` from here, and every
 * one of those call sites is marked with a REGISTRATION-PAUSE comment.
 *
 * TO RE-ENABLE registration:
 *   set `isEnabled: true` for the stages you want open, below. Nothing else.
 *
 * TO REMOVE the pause entirely:
 *   1. grep the repo for REGISTRATION-PAUSE
 *   2. drop each guard, keeping the branch that renders the real flow
 *   3. delete this file and src/config/__tests__/registration.test.ts
 *
 * NOTE ON SCOPE: this is a client-side pause. It closes the routes and entry
 * points a person can reach in the portal; it does not stop a direct call to
 * the Cognito API, and it deliberately does not touch the user pools — the
 * main pool is shared with other apps (see FNP-1222). Server-side enforcement
 * is FNP-891 / FNP-894.
 */
import {stage} from './stage';

type RegistrationConfig = {
    /** When false: /sign-up is closed, sign-up links are hidden, and the
     *  federated sign-up buttons are not rendered. */
    isEnabled: boolean;
};

type ConfigShape = {
    [key in 'prod' | 'test' | 'dev' | 'ide']: RegistrationConfig;
};

const registrationByStage: ConfigShape = {
    prod: {isEnabled: false},
    test: {isEnabled: false},
    dev: {isEnabled: false},
    ide: {isEnabled: false},
};

/**
 * Copy shown in place of the sign-up flow while it is closed. Kept here so the
 * wording is changed in one place, and so removing the pause removes the copy.
 */
export const registrationClosedContent = {
    title: 'Registration is temporarily closed',
    message: 'We are not accepting new sign-ups at the moment. Please check back soon.',
    signInPrompt: 'Already have an account?',
    signInLabel: 'Sign In',
} as const;

/** Fall back to closed if VITE_STAGE is ever unset or unrecognised. */
const registration: RegistrationConfig = registrationByStage[stage] ?? {isEnabled: false};

export const isRegistrationEnabled = (): boolean => registration.isEnabled;

export default registration;
